import { Copy, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import type {
  ConversationSummaryDto,
  DeliveryStatus,
  ShipmentPaymentType,
} from "@mandoob/shared";

import { useUpdateDelivery } from "@/api/inbox";
import { ResponsiveDialog } from "@/components/shared/responsive-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/language";
import { errorMessage } from "@/lib/errors";
import { formatNumber } from "@/lib/format";
import { hapticNotification, hapticSelection } from "@/lib/haptics";
import { formatPhoneNumberForDisplay } from "@/lib/phone-utils";
import { cn } from "@/lib/utils";

import { DELIVERY_STATUS_KEYS } from "./delivery-status-badge";
import { isConversationArchived } from "./lib";

const STATUSES: DeliveryStatus[] = ["New", "Ongoing", "Delivered", "Cancelled"];

const PAYMENT_TYPE_KEYS: Record<ShipmentPaymentType, string> = {
  COD: "Mandoob:Delivery.PaymentCod",
  Paid: "Mandoob:Delivery.PaymentPaid",
  Other: "Mandoob:Delivery.PaymentOther",
};

/** 1200 -> "1.2", 2000 -> "2" — kilos without trailing zeros. */
const formatWeightKg = (grams: number): string =>
  String(Math.round(grams / 10) / 100);

const STATUS_SELECTED_STYLE: Record<DeliveryStatus, string> = {
  New: "tw:border-info tw:bg-info-soft tw:text-info",
  Ongoing: "tw:border-warning tw:bg-warning-soft tw:text-warning",
  Delivered: "tw:border-success tw:bg-success-soft tw:text-success-foreground",
  Cancelled: "tw:border-destructive tw:bg-destructive-soft tw:text-destructive",
};

/**
 * Delivery details for a conversation (= delivery). The info fields were
 * snapshotted at start-conversation and are immutable — the only edit a
 * driver keeps here is the STATUS.
 */
export function DeliveryDetailsSheet({
  open,
  onClose,
  conversation,
}: {
  open: boolean;
  onClose: () => void;
  conversation: ConversationSummaryDto;
}) {
  const { t, dir, language } = useLanguage();

  /** Halalas -> "150 SAR" / "150 ر.س" in the UI language. */
  const money = (halalas: number) =>
    `${formatNumber(halalas / 100, language, { maximumFractionDigits: 2 })} ${
      language === "ar" ? "ر.س" : "SAR"
    }`;

  const [status, setStatus] = useState<DeliveryStatus>("New");
  // Cancelling is destructive (closes the chat, frees the number) — saving it
  // detours through this confirmation.
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  // Re-seed from the conversation every time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setStatus(conversation.deliveryStatus);
    setConfirmCancelOpen(false);
  }, [open, conversation]);

  const updateDelivery = useUpdateDelivery({
    mutation: {
      onSuccess: () => {
        hapticNotification("success");
        toast.success(t("SuccessfullySaved"));
        onClose();
      },
      onError: (error) => {
        hapticNotification("error");
        toast.error(errorMessage(error, t));
      },
    },
  });

  // Cancelled is final and an archived chat is read-only — the API refuses
  // the update anyway, so the controls lock too.
  const statusLocked =
    conversation.deliveryStatus === "Cancelled" ||
    isConversationArchived(conversation);

  const dirty = !statusLocked && status !== conversation.deliveryStatus;

  const handleSave = () => {
    if (!dirty || updateDelivery.isPending) return;
    if (status === "Cancelled") {
      setConfirmCancelOpen(true);
      return;
    }
    updateDelivery.mutate({ conversationId: conversation.id, status });
  };

  const confirmCancel = () => {
    setConfirmCancelOpen(false);
    updateDelivery.mutate({ conversationId: conversation.id, status });
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(conversation.id);
      hapticSelection();
      toast.success(t("Mandoob:Delivery.IdCopied"));
    } catch {
      toast.error(t("AnErrorOccurred"));
    }
  };

  const infoRow = (label: string, value: string | null, ltr = false) => (
    <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-3">
      <span className="tw:shrink-0 tw:text-sm tw:text-muted-foreground">
        {label}
      </span>
      <span
        dir={ltr ? "ltr" : undefined}
        className="tw:min-w-0 tw:truncate tw:text-sm tw:font-medium tw:text-foreground"
        style={ltr ? { unicodeBidi: "isolate" } : undefined}
      >
        {value || "—"}
      </span>
    </div>
  );

  return (
    <>
      <ResponsiveDialog
        open={open}
        onClose={onClose}
        title={t("Mandoob:Delivery.DetailsTitle")}
        footer={
          <Button
            type="button"
            onClick={handleSave}
            disabled={!dirty}
            loading={updateDelivery.isPending}
            className="tw:w-full"
          >
            {t("Save")}
          </Button>
        }
      >
        <div dir={dir} className="tw:flex tw:flex-col tw:gap-4">
          <div className="tw:flex tw:flex-col tw:gap-1 tw:rounded-xl tw:bg-muted tw:p-3">
            <span className="tw:text-xs tw:font-semibold tw:text-muted-foreground">
              {t("Mandoob:Delivery.Id")}
            </span>
            <span className="tw:flex tw:items-center tw:gap-2">
              <span
                dir="ltr"
                className="tw:min-w-0 tw:flex-1 tw:truncate tw:font-mono tw:text-xs tw:text-foreground"
                style={{ unicodeBidi: "isolate" }}
              >
                {conversation.id}
              </span>
              <button
                type="button"
                onClick={() => void copyId()}
                aria-label={t("Mandoob:Delivery.CopyId")}
                className="tw:flex tw:size-7 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:text-muted-foreground tw:transition-colors tw:hover:bg-background tw:hover:text-foreground"
              >
                <Copy className="tw:size-3.5" />
              </button>
            </span>
            <span className="tw:mt-1 tw:flex tw:items-baseline tw:gap-2 tw:text-sm">
              <span className="tw:text-muted-foreground">
                {t("Mandoob:Delivery.Client")}
              </span>
              <span className="tw:min-w-0 tw:truncate tw:font-medium tw:text-foreground">
                {conversation.name}
              </span>
              <span
                dir="ltr"
                className="tw:shrink-0 tw:text-xs tw:text-muted-foreground"
                style={{ unicodeBidi: "isolate" }}
              >
                {formatPhoneNumberForDisplay(conversation.phoneNumber)}
              </span>
            </span>
          </div>

          <div className="tw:flex tw:flex-col tw:gap-2">
            <Label>{t("Mandoob:Delivery.Status")}</Label>
            <div className="tw:grid tw:grid-cols-2 tw:gap-2">
              {STATUSES.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStatus(value)}
                  disabled={statusLocked}
                  aria-pressed={status === value}
                  className={cn(
                    "tw:rounded-xl tw:border tw:px-3 tw:py-2.5 tw:text-sm tw:font-medium tw:transition-colors",
                    status === value
                      ? STATUS_SELECTED_STYLE[value]
                      : "tw:border-border tw:bg-background tw:text-muted-foreground tw:hover:bg-muted/60",
                    statusLocked && "tw:pointer-events-none",
                    statusLocked &&
                      (status === value ? "tw:opacity-60" : "tw:opacity-30"),
                  )}
                >
                  {t(DELIVERY_STATUS_KEYS[value])}
                </button>
              ))}
            </div>
            {statusLocked ? (
              <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                {t("Mandoob:Delivery.StatusLocked")}
              </p>
            ) : null}
          </div>

          {/* Snapshotted at start — read-only. */}
          <div className="tw:flex tw:flex-col tw:gap-2.5 tw:rounded-xl tw:border tw:border-border tw:p-3">
            {infoRow(
              t("Mandoob:Delivery.TrackingNumber"),
              conversation.trackingNumber,
              true,
            )}
            {infoRow(t("Mandoob:Delivery.Company"), conversation.company)}
            {infoRow(t("Mandoob:Delivery.DriverName"), conversation.driverName)}
            {infoRow(
              t("Mandoob:Delivery.CallingPhone"),
              conversation.driverCallingPhone
                ? formatPhoneNumberForDisplay(conversation.driverCallingPhone)
                : null,
              true,
            )}
            {/* Gathered from the label / file — shown only when known, never
                asked for. */}
            {conversation.senderName
              ? infoRow(
                  t("Mandoob:Delivery.Sender"),
                  conversation.senderType === "Company"
                    ? `${conversation.senderName} · ${t("Mandoob:Delivery.SenderCompany")}`
                    : conversation.senderName,
                )
              : null}
            {conversation.senderWebsite
              ? infoRow(
                  t("Mandoob:Delivery.SenderWebsite"),
                  conversation.senderWebsite,
                  true,
                )
              : null}
            {conversation.paymentType
              ? infoRow(
                  t("Mandoob:Delivery.PaymentType"),
                  t(PAYMENT_TYPE_KEYS[conversation.paymentType]),
                )
              : null}
            {conversation.costHalalas != null
              ? infoRow(
                  t("Mandoob:Delivery.Cost"),
                  money(conversation.costHalalas),
                  true,
                )
              : null}
            {conversation.shippingCostHalalas != null
              ? infoRow(
                  t("Mandoob:Delivery.ShippingCost"),
                  money(conversation.shippingCostHalalas),
                  true,
                )
              : null}
            {conversation.weightGrams != null
              ? infoRow(
                  t("Mandoob:Delivery.Weight"),
                  t(
                    "Mandoob:Delivery.WeightKg",
                    formatWeightKg(conversation.weightGrams),
                  ),
                  true,
                )
              : null}
            {conversation.nationalAddress
              ? infoRow(
                  t("Mandoob:Delivery.NationalAddress"),
                  conversation.nationalAddress,
                  true,
                )
              : null}
            {/* Written by the webhook when the client shares their location. */}
            {conversation.clientLocation ? (
              <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-3">
                <span className="tw:shrink-0 tw:text-sm tw:text-muted-foreground">
                  {t("Mandoob:Delivery.ClientLocation")}
                </span>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${conversation.clientLocation.latitude},${conversation.clientLocation.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="tw:flex tw:min-w-0 tw:items-center tw:gap-1 tw:text-sm tw:font-medium tw:text-brand tw:no-underline tw:hover:underline"
                >
                  <MapPin className="tw:size-3.5 tw:shrink-0" />
                  <span
                    dir="ltr"
                    className="tw:min-w-0 tw:truncate"
                    style={{ unicodeBidi: "isolate" }}
                  >
                    {conversation.clientLocation.name ??
                      conversation.clientLocation.address ??
                      `${conversation.clientLocation.latitude.toFixed(5)}, ${conversation.clientLocation.longitude.toFixed(5)}`}
                  </span>
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </ResponsiveDialog>

      {/* Cancelling closes the chat and releases the client's number — worth an
        explicit confirmation before it goes through. */}
      <Dialog
        open={confirmCancelOpen}
        onOpenChange={(next) => !next && setConfirmCancelOpen(false)}
      >
        <DialogContent dir={dir} className="tw:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {t("Mandoob:Delivery.CancelConfirmTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("Mandoob:Delivery.CancelConfirmBody")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="tw:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmCancelOpen(false)}
            >
              {t("Mandoob:Back")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmCancel}
              loading={updateDelivery.isPending}
            >
              {t("Mandoob:Delivery.CancelConfirmAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
